import { TESTIMONIALS_CAROUSEL, TESTIMONIALS_META, type TestimonialsCarouselProps } from '../constants/home'
import { RATING_STAR_SIZE } from '../constants/icons'
import { testimonialTrackVars } from '../helpers/carousel'
import { cx } from '../helpers/format'
import { ratingStars } from '../helpers/rating'
import { useTestimonialsCarousel } from '../hooks/useTestimonialsCarousel'
import { Icon } from './Icon'

export function TestimonialsCarousel({ testimonials }: TestimonialsCarouselProps) {
  const {
    activeIndex,
    goTo,
    goNext,
    goPrev,
    pause,
    resume,
    canNavigate,
    trackStart,
    visibleCount,
    isVisible,
    onKeyDown,
  } = useTestimonialsCarousel(testimonials.length)

  if (testimonials.length === 0) return null

  return (
    <section className="section testimonials">
      <div className="container">
        <div className="section__head section__head--center">
          <div>
            <h2>{TESTIMONIALS_META.title}</h2>
            <p className="section__sub">{TESTIMONIALS_META.subtitle}</p>
          </div>
        </div>

        <div
          className="testimonials-carousel"
          style={testimonialTrackVars(trackStart, visibleCount)}
          onMouseEnter={pause}
          onMouseLeave={resume}
          onKeyDown={onKeyDown}
          aria-roledescription="carousel"
          aria-label={TESTIMONIALS_META.title}
        >
          <p className="sr-only" aria-live="polite">
            {TESTIMONIALS_CAROUSEL.slideAria(activeIndex, testimonials.length)}
          </p>
          <div className="testimonials-carousel__viewport">
            <div className="testimonials-carousel__track">
              {testimonials.map((item, index) => {
                const active = index === activeIndex
                const visible = isVisible(index)

                return (
                  <blockquote
                    key={item.id}
                    className={cx(
                      'testimonial-card',
                      'testimonial-card--carousel',
                      active && 'is-active',
                    )}
                    role="group"
                    aria-hidden={visible ? undefined : true}
                    aria-current={active ? 'true' : undefined}
                    aria-roledescription={TESTIMONIALS_CAROUSEL.slideRole}
                    onClick={active ? undefined : () => goTo(index)}
                  >
                    {item.rating != null ? (
                      <div
                        className="testimonial-card__rating"
                        aria-label={TESTIMONIALS_CAROUSEL.ratingAria(
                          item.rating,
                          TESTIMONIALS_CAROUSEL.ratingMax,
                        )}
                      >
                        {ratingStars(item.rating, TESTIMONIALS_CAROUSEL.ratingMax).map((star) => (
                          <Icon
                            key={star.id}
                            name="star"
                            size={RATING_STAR_SIZE}
                            filled={star.filled}
                          />
                        ))}
                      </div>
                    ) : null}
                    <p className="testimonial-card__quote">&ldquo;{item.quote}&rdquo;</p>
                    <footer className="testimonial-card__footer">
                      {item.avatarUrl ? (
                        <img
                          src={item.avatarUrl}
                          alt=""
                          className="testimonial-card__avatar"
                        />
                      ) : (
                        <span
                          className="testimonial-card__avatar testimonial-card__avatar--fallback"
                          aria-hidden
                        >
                          {item.customerName.charAt(0)}
                        </span>
                      )}
                      <div>
                        <cite>{item.customerName}</cite>
                        {item.customerLocation ? (
                          <span className="testimonial-card__location">{item.customerLocation}</span>
                        ) : null}
                      </div>
                    </footer>
                  </blockquote>
                )
              })}
            </div>
          </div>

          {canNavigate ? (
            <div className="testimonials-carousel__controls">
              <button
                type="button"
                className="testimonials-carousel__nav"
                aria-label={TESTIMONIALS_CAROUSEL.prevAria}
                onClick={goPrev}
              >
                <Icon name="chevronLeft" size={18} />
              </button>
              <div
                className="testimonials-carousel__dots"
                role="tablist"
                aria-label={TESTIMONIALS_CAROUSEL.dotsAria}
              >
                {testimonials.map((entry, index) => (
                  <button
                    key={entry.id}
                    type="button"
                    role="tab"
                    aria-selected={index === activeIndex}
                    aria-label={TESTIMONIALS_CAROUSEL.dotAria(index)}
                    className={cx(
                      'testimonials-carousel__dot',
                      index === activeIndex && 'is-active',
                    )}
                    onClick={() => goTo(index)}
                  />
                ))}
              </div>
              <button
                type="button"
                className="testimonials-carousel__nav"
                aria-label={TESTIMONIALS_CAROUSEL.nextAria}
                onClick={goNext}
              >
                <Icon name="chevronRight" size={18} />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
